import './applyRefinanceGoalPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { RefinanceGoal } from '../../../store/applyStore/enums/RefinanceGoal';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { refinanceGoal, setRefinanceGoal } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      refinanceGoalValue: refinanceGoal,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<RefinanceGoal>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const refinanceGoalValue = event.target.value;

    setIsInitialSelection(isEmpty(refinanceGoal) && isValidClickEvent(event));

    setRefinanceGoal(refinanceGoalValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { refinanceGoalValue } = values;

    if (refinanceGoalValue) {
      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <div className="apply-refinance-goal-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyRefinanceGoal.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyRefinanceGoal.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.applyRefinanceGoal.header.drawerButton}
              type="button"
              onClick={(): void => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyRefinanceGoal.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form noValidate onSubmit={handleSubmit(formSubmit)}>
              <div className="form-content">
                <h1 data-testid={locator.pages.applyRefinanceGoal.form.h1}>
                  {t('ApplyRefinanceGoalPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyRefinanceGoal.form.h2}>
                  {t('ApplyRefinanceGoalPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    name="refinanceGoalValue"
                    options={[
                      {
                        label: t('ApplyRefinanceGoalPage.lowerPayment'),
                        value: RefinanceGoal.LOWER_PAYMENT,
                        testId:
                          locator.pages.applyRefinanceGoal.form.radioOption1,
                      },
                      {
                        label: t('ApplyRefinanceGoalPage.lowerRate'),
                        value: RefinanceGoal.LOWER_RATE,
                        testId:
                          locator.pages.applyRefinanceGoal.form.radioOption2,
                      },
                      {
                        label: t('ApplyRefinanceGoalPage.checkingOptions'),
                        value: RefinanceGoal.CHECK_OPTIONS,
                        testId:
                          locator.pages.applyRefinanceGoal.form.radioOption3,
                      },
                    ]}
                    register={register}
                    testId={
                      locator.pages.applyRefinanceGoal.form.radioGroupRoot
                    }
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.applyRefinanceGoal.form.submitButton
                    }
                    disabled={isEmpty(refinanceGoal)}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplyRefinanceGoalPage.continue')}
                  </Button>
                </div>
              </div>
              <footer
                data-testid={locator.pages.applyRefinanceGoal.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyRefinanceGoalPage';
